import React, { useEffect, useState } from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { routes } from './Routes/Routes';

// Custom Error Boundary for Chunk Load Errors
class ChunkErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      retryCount: 0,
      isFirefox: navigator.userAgent.includes('Firefox')
    };
  }

  static getDerivedStateFromError(error) {
    if (error.name === 'ChunkLoadError') {
      return { hasError: true, error };
    }
    return { hasError: false, error: null };
  }

  retryLoading = () => {
    // For Firefox, we need to use a cache-busting technique
    if (this.state.isFirefox) {
      // Force clear cache by adding a unique parameter
      window.location.href = window.location.href.split('?')[0] + `?chunkRetry=${Date.now()}`;
    } else {
      window.location.reload();
    }
  };

  clearCacheAndReload = () => {
    // Try to clear service worker cache if it exists
    if ('caches' in window) {
      caches.keys().then(function (names) {
        for (let name of names) caches.delete(name);
      });
    }

    // Clear localStorage and sessionStorage if needed
    localStorage.clear();
    sessionStorage.clear();

    // Force reload with cache busting
    window.location.href = window.location.href.split('?')[0] + `?cacheBust=${Date.now()}`;
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={styles.container}>
          <div style={styles.content}>
            <div style={styles.icon}>⚠️</div>
            <h1 style={styles.title}>Application Update Required</h1>
            <p style={styles.message}>
              The application has been updated. Please refresh to load the latest version.
            </p>

            {this.state.isFirefox && (
              <div style={styles.firefoxWarning}>
                <strong>Firefox Detected:</strong> Firefox may cache files aggressively.
                Try the "Clear Cache & Hard Refresh" option if a normal refresh doesn't work.
              </div>
            )}

            <p style={styles.detail}>
              Error: {this.state.error.message}
            </p>

            <div style={styles.buttonGroup}>
              <button
                onClick={this.retryLoading}
                style={styles.primaryButton}
              >
                Refresh Application
              </button>

              {this.state.isFirefox && (
                <button
                  onClick={this.clearCacheAndReload}
                  style={styles.secondaryButton}
                >
                  Clear Cache & Hard Refresh
                </button>
              )}
            </div>

            {this.state.retryCount > 0 && (
              <p style={styles.note}>
                If this continues, try clearing your browser cache or contact support.
              </p>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

// Global error handler for chunk loading errors
const registerChunkErrorHandler = () => {
  window.addEventListener('unhandledrejection', (event) => {
    if (event.reason && event.reason.name === 'ChunkLoadError') {
      const chunkFailedMessage = /Loading chunk (\d+) failed/;
      const match = event.reason.message.match(chunkFailedMessage);

      if (match) {
        const chunkId = match[1];
        console.warn(`Chunk ${chunkId} failed to load, will retry...`);

        // For Firefox, we can try to recover by reloading with cache busting
        if (navigator.userAgent.includes('Firefox')) {
          setTimeout(() => {
            window.location.href = window.location.href.split('?')[0] + `?chunkRetry=${Date.now()}`;
          }, 1000);
        }

        event.preventDefault();
      }
    }
  });
};

// Function to prevent caching issues in Firefox
const preventFirefoxCaching = () => {
  if (navigator.userAgent.includes('Firefox')) {
    // Add a cache-busting parameter to script tags
    const originalCreateElement = document.createElement;
    document.createElement = function () {
      const element = originalCreateElement.apply(this, arguments);
      if (arguments[0] === 'script') {
        const originalSetAttribute = element.setAttribute;
        element.setAttribute = function (name, value) {
          if (name === 'src') {
            const separator = value.includes('?') ? '&' : '?';
            value += separator + 'cacheBust=' + Date.now();
          }
          originalSetAttribute.call(this, name, value);
        };
      }
      return element;
    };
  }
};

const router = createBrowserRouter(routes, {
  basename: process.env.REACT_APP_BASE_PREFIX || '/',
});

// Styling for the error component
const styles = {
  container: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    backgroundColor: '#f8f9fa',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    padding: '20px',
  },
  content: {
    textAlign: 'center',
    maxWidth: '500px',
    padding: '40px',
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  },
  icon: {
    fontSize: '60px',
    marginBottom: '20px',
  },
  title: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#212529',
    margin: '0 0 15px 0',
  },
  message: {
    fontSize: '16px',
    color: '#6c757d',
    lineHeight: '1.5',
    margin: '0 0 20px 0',
  },
  firefoxWarning: {
    fontSize: '14px',
    color: '#856404',
    backgroundColor: '#fff3cd',
    padding: '12px',
    borderRadius: '6px',
    margin: '0 0 20px 0',
    textAlign: 'left'
  },
  detail: {
    fontSize: '14px',
    color: '#868e96',
    backgroundColor: '#f8f9fa',
    padding: '10px',
    borderRadius: '6px',
    margin: '0 0 25px 0',
    wordBreak: 'break-all',
  },
  buttonGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginBottom: '15px'
  },
  primaryButton: {
    padding: '12px 24px',
    backgroundColor: '#007bff',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  secondaryButton: {
    padding: '12px 24px',
    backgroundColor: '#6c757d',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '16px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  note: {
    fontSize: '14px',
    color: '#868e96',
    fontStyle: 'italic',
    margin: '0',
  }
};

function App() {
  useEffect(() => {
    registerChunkErrorHandler();
    preventFirefoxCaching();
  }, []);

  return (
    <ChunkErrorBoundary>
      <RouterProvider router={router} />
    </ChunkErrorBoundary>
  );
}

export default App;